import { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'
import type { CotizacionListItem, EditarCotizacionPayload } from '../types'
import { calcularPrecioMensual } from '../lib/pricing-tiers'

const schema = z.object({
  volumen_correo: z.coerce.number().min(0, 'No puede ser negativo'),
  volumen_whatsapp: z.coerce.number().min(0, 'No puede ser negativo'),
  volumen_correo_whatsapp: z.coerce.number().min(0, 'No puede ser negativo'),
}).refine(
  (data) => data.volumen_correo > 0 || data.volumen_whatsapp > 0 || data.volumen_correo_whatsapp > 0,
  { message: 'Debe tener al menos un volumen mayor a 0', path: ['volumen_correo'] }
)

type FormData = z.infer<typeof schema>

interface CotizacionEditModalProps {
  cotizacion: CotizacionListItem | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: EditarCotizacionPayload) => void
  isSubmitting: boolean
}

export function CotizacionEditModal({ cotizacion, open, onOpenChange, onSubmit, isSubmitting }: CotizacionEditModalProps) {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { volumen_correo: 0, volumen_whatsapp: 0, volumen_correo_whatsapp: 0 },
  })

  // Guardar valores originales para comparar al enviar
  const originalRef = useRef({ volumen_correo: 0, volumen_whatsapp: 0, volumen_correo_whatsapp: 0 })

  const vc = watch('volumen_correo') || 0
  const vw = watch('volumen_whatsapp') || 0
  const vcw = watch('volumen_correo_whatsapp') || 0
  const precioPreview = calcularPrecioMensual(vc, vw, vcw)

  useEffect(() => {
    if (open && cotizacion) {
      const valores = {
        volumen_correo: cotizacion.volumen_correo,
        volumen_whatsapp: cotizacion.volumen_whatsapp,
        volumen_correo_whatsapp: cotizacion.volumen_correo_whatsapp,
      }
      originalRef.current = valores
      reset(valores)
    }
  }, [open, cotizacion, reset])

  const handleFormSubmit = (data: FormData) => {
    // Solo enviar campos que cambiaron
    const payload: EditarCotizacionPayload = {}
    if (data.volumen_correo !== originalRef.current.volumen_correo) {
      payload.volumen_correo = data.volumen_correo
    }
    if (data.volumen_whatsapp !== originalRef.current.volumen_whatsapp) {
      payload.volumen_whatsapp = data.volumen_whatsapp
    }
    if (data.volumen_correo_whatsapp !== originalRef.current.volumen_correo_whatsapp) {
      payload.volumen_correo_whatsapp = data.volumen_correo_whatsapp
    }
    onSubmit(payload)
  }

  if (!cotizacion) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar Cotización #{cotizacion.id}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {cotizacion.nombre} — {cotizacion.email}
          </p>

          <div className="space-y-2">
            <Label htmlFor="volumen_correo">Volumen Correo</Label>
            <Input
              id="volumen_correo"
              type="number"
              min={0}
              {...register('volumen_correo')}
              className={errors.volumen_correo ? 'border-red-500' : ''}
            />
            {errors.volumen_correo && (
              <p className="text-sm text-red-500">{errors.volumen_correo.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="volumen_whatsapp">Volumen WhatsApp</Label>
            <Input
              id="volumen_whatsapp"
              type="number"
              min={0}
              {...register('volumen_whatsapp')}
              className={errors.volumen_whatsapp ? 'border-red-500' : ''}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="volumen_correo_whatsapp">Volumen Correo + WhatsApp</Label>
            <Input
              id="volumen_correo_whatsapp"
              type="number"
              min={0}
              {...register('volumen_correo_whatsapp')}
              className={errors.volumen_correo_whatsapp ? 'border-red-500' : ''}
            />
          </div>

          <div className="rounded-md bg-muted p-3">
            <p className="text-sm text-muted-foreground">Precio mensual estimado</p>
            <p className="text-2xl font-bold">
              ${precioPreview.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" className="bg-teal-800 hover:bg-teal-900 text-white" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
